import React from "react";
import { Box, Stack, Typography, Button } from "@mui/material";
import moment from "moment";

import ReportTableContainerTitle from "../atoms/ReportTableContainerTitle";
import NoReportsView from "../molecules/NoReportsView";
import { DeleteButton } from "../../atoms/EditButton";
import AvatarName from "../molecules/AvatarName";
import { ReportStates } from "../../hooks/useReportHistory";
import MightyDataGrid from "./MightyDataGrid";

const ReportDataGrid = ({ name, reports, columns, onRowClick }) => {
  return (
    <MightyDataGrid
      entityName={`report_${name}`}
      rows={reports}
      columns={columns}
      onRowClick={onRowClick}
      pagination={true}
      pageSize={15}
      pageSizeOptions={[15, 25, 50]}
      viewportOffset={460} // Adjust based on your header height + search input
    />
  );
};

const ReportTableContainer = ({
  loading = true,
  state = ReportStates.unprocessed,
  title,
  reports,
  showAlertCount,
  reportClicked,
  deleteReportClicked,
  processReportClicked,
}) => {
  function getPeriod(report) {
    try {
      let start = "";
      let end = "";

      if (report.startDate) {
        start = moment(report.startDate?.toDate());
      }
      if (report.endDate) {
        end = moment(report.endDate?.toDate());
      }
      return `${start.format("MM/DD")} - ${end.format("MM/DD")}`;
    } catch {
      return "";
    }
  }

  function getHoursSinceCreatedOn(report) {
    const createdOn = moment(report.createdOn);
    const now = moment();
    const duration = moment.duration(now.diff(createdOn));
    const hours = duration.asHours().toFixed(0);
    return hours;
  }

  function getCreatedOn(report) {
    const createdOn = moment(new Date(report.createdOn)).clone();
    return createdOn.format("MM/DD/YYYY");
  }

  function getProcessedOn(report) {
    if (!report.statusUpdate) {
      return "-";
    }
    const statusDate = report.statusUpdate?.toDate
      ? report.statusUpdate.toDate()
      : new Date(report.statusUpdate);
    return moment(statusDate).format("MM/DD/YYYY");
  }

  function getProcessedBy(report) {
    const processorFirstName = report.processorUser?.firstName || "";
    const processorLastName = report.processorUser?.lastName || "";
    const fullName = `${processorFirstName} ${processorLastName}`.trim();
    return fullName || "-";
  }

  const columns = React.useMemo(() => {
    const result = [
      {
        field: "avatar",
        headerName: "",
        width: 50,
        hideable: false,
        headerAlign: "left",
        align: "left",
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          return (
            <Box style={{ display: "flex", alignItems: "center" }}>
              <AvatarName
                showName={false}
                url={params.row.userData.avatarUrl}
                size={32}
                firstName={params.row.clientFirstName}
                lastName={params.row.clientLastName}
              />
            </Box>
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        minWidth: 100,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          return `${params.row.clientFirstName} ${params.row.clientLastName}`;
        },
      },
      {
        field: "period",
        headerName: "Period",
        minWidth: 200,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => {
          return getPeriod(params.row);
        },
      },
      {
        field: "createdOn",
        headerName: "Created",
        minWidth: 300,
        hideable: false,
        headerAlign: "left",
        align: "left",
        type: "dateTime",
        defaultSort: "asc",
        valueGetter: (params) => {
          return params.row.createdOn;
        },
        renderCell: (params) => {
          return getCreatedOn(params.row);
        },
      },
    ];

    if (state === ReportStates.unprocessed) {
      result.push({
        field: "elapsed",
        headerName: "Hours since Creation",
        minWidth: 300,
        hideable: false,
        headerAlign: "left",
        align: "left",
        type: "number",
        defaultSort: "asc",
        renderCell: (params) => {
          const hours = getHoursSinceCreatedOn(params.row);
          if (hours > 24) {
            return <Typography color="error">{hours}</Typography>;
          }
          return <Typography>{hours}</Typography>;
        },
      });
      result.push({
        field: "quickProcess",
        headerName: "Actions",
        minWidth: 180,
        hideable: false,
        headerAlign: "left",
        align: "left",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => {
                event.ignore = true;
                event.stopPropagation();
                if (processReportClicked) {
                  processReportClicked(params.row);
                }
              }}
            >
              Mark Processed
            </Button>
          );
        },
      });
    } else {
      result.push({
        field: "processedOn",
        headerName: "Processed On",
        minWidth: 180,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => getProcessedOn(params.row),
      });
      result.push({
        field: "processedBy",
        headerName: "Processed By",
        minWidth: 180,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        valueGetter: (params) => getProcessedBy(params.row),
      });
    }

    result.push({
      minWidth: 35,
      headerAlign: "center",
      align: "center",
      field: "delete",
      headerName: "",
      sortable: false,
      hideable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.status === "unprocessed") {
          return null;
        }
        return (
          <Stack
            spacing={0}
            direction="row"
            alignItems="flex-end"
            justifyContent="flex-end"
          >
            <DeleteButton
              onClick={(event) => {
                event.ignore = true;
                event.stopPropagation();
                deleteReportClicked(params.row);
              }}
            />
          </Stack>
        );
      },
    });

    return result;
  }, [state, deleteReportClicked, processReportClicked]);

  if (loading) {
    return null;
  }

  if (reports.length === 0) {
    // Show no reports UI.
    return <NoReportsView state={state} />;
  }

  return (
    <Box>
      {reports.length > 0 && (
        <>
          <Box style={{ marginTop: 40, marginBottom: 20 }}>
            <ReportTableContainerTitle
              title={title}
              tier="tier1"
              showAlertCount={showAlertCount}
              count={reports.length}
            />
          </Box>

          <ReportDataGrid
            name="tier1"
            reports={reports}
            columns={columns}
            onRowClick={(report) => {
              if (report?.id) {
                reportClicked(report);
              }
            }}
          />
        </>
      )}
    </Box>
  );
};

export default ReportTableContainer;
